import { apis, RequestOptions } from "../../../../backend";
import { UnroutedContactFlow, UnroutedContactFlowStage } from "../../../../models/UnroutedContactFlow";

/**
 * @returns A list of flows for handling unrouted calls.
 */
export async function listUnroutedContactFlows() {
  const flows = await apis.dial.get<UnroutedContactFlow[]>('/unrouted-contact-flows-v1/');
  return flows.map(flow => new UnroutedContactFlow(flow));
}

/**
 * @returns A specific flow for handling unrouted calls.
 */
export async function getUnroutedContactFlow(flowId: string, signal?: AbortSignal) {
  const flow = await apis.dial.get<UnroutedContactFlow>(`/unrouted-contact-flows-v1/${flowId}`, {}, { signal });
  return new UnroutedContactFlow(flow);
}

/**
 * Creates a new unrouted contact flow.
 */
export async function createUnroutedContactFlow(flow: UnroutedContactFlow) {
  return apis.dial.post<{ flowId: string; stageIds: string[]; }>('/unrouted-contact-flows-v1/', flow);
}

/**
 * Updates an existing unrouted contact flow.
 */
export async function updateUnroutedContactFlow(flowId: string, update: Partial<UnroutedContactFlow>) {
  return apis.dial.put(`/unrouted-contact-flows-v1/${flowId}`, update);
}

/**
 * Deletes an existing unrouted contact flow.
 */
export async function deleteUnroutedContactFlow(flowId: string) {
  return apis.dial.del(`/unrouted-contact-flows-v1/${flowId}`);
}

/**
 * @returns The current UCF stage for a given flow, lead list and number.
 */
export async function getCurrentUnroutedContactFlowStage(flowId: string, number: string, leadListId: string, opts?: RequestOptions) {
  const stage = await apis.dial.get<UnroutedContactFlowStage>(`/unrouted-contact-flows-v1/${flowId}/stages/current`,
    { number, dialGroupId: leadListId }, opts);
  return new UnroutedContactFlowStage(stage);
}

/**
 * Creates a new stage associated with an unrouted contact flow.
 */
export async function createUnroutedContactFlowStage(flowId: string, stage: UnroutedContactFlowStage, index: number) {
  return apis.dial.post<{ stageId: string; }>(`/unrouted-contact-flows-v1/${flowId}/stages`, { ...stage, index });
}

/**
 * Updates an existing stage associated with an unrouted contact flow.
 */
export async function updatesUnroutedContactFlowStage(flowId: string, stageId: string, update: Partial<UnroutedContactFlowStage>) {
  return apis.dial.put(`/unrouted-contact-flows-v1/${flowId}/stages/${stageId}`, update);
}

/**
 * Deletes an existing stage associated with an unrouted contact flow.
 */
export async function deleteUnroutedContactFlowStage(flowId: string, stageId: string) {
  return apis.dial.del(`/unrouted-contact-flows-v1/${flowId}/stages/${stageId}`);
}

/**
 * Updates the order of a unrouted contact flow's stages.
 */
export async function updateUnroutedContactFlowStagesOrdering(flowId: string, stageIds: string[]) {
  return apis.dial.put(`/unrouted-contact-flows-v1/${flowId}/stages/ordering`, { stageIds });
}